// ----------------------------------------------------------------------------
// Product Form Extension: Sticky Cart
// ----------------------------------------------------------------------------
// Manages the visibility of a "sticky cart" element based on the user's scroll position.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:init' → Listens for the product form initialization event to
//                        initialize the sticky cart classes and observer.
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Toggles visibility classes ('product__sticky--show' and 'product__sticky--hide')
//   on the sticky cart based on the intersection ratio and scroll position.
// ----------------------------------------------------------------------------
// Gotchas:
// - Relies on IntersectionObserver to check visibility, so it requires supported browsers.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';

// Styles
import './_product-form--sticky.scss';

// -------------------------------------
// Sticky Cart Class
// -------------------------------------

var FOCUSABLE_SELECTOR = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';
class StickyCart {
  constructor(iamProductForm) {
    this.iamForm = iamProductForm;
    this.document = this.iamForm.ownerDocument;
    this.bridgeNextFocusable = null;
    this.bridgePreviousFocusable = null;
    this.hasVisitedStickyControls = false;
    this.handleKeydownRef = this.handleKeydown.bind(this);
    this.initStickyCart();
  }
  initStickyCart() {
    var options = {
      rootMargin: '100px 0px 0px 0px',
      threshold: [0, 0.01, 1]
    };
    this.target = this.iamForm.querySelector('#productStickyContainer');
    if (!this.target) return;
    this.observer = new IntersectionObserver(this.showHideStickyCart.bind(this), options);
    this.observer.observe(this.target);
    this.document.addEventListener('keydown', this.handleKeydownRef, true);
  }
  showHideStickyCart(entries) {
    var entry = entries[0];
    if (entry.intersectionRatio >= 1) {
      entry.target.classList.remove('product__sticky--show');
      this.clearBridgeFocusElements();
      this.hasVisitedStickyControls = false;
    } else if (entry.intersectionRatio <= 0 && entry.boundingClientRect.y <= 0) {
      entry.target.classList.add('product__sticky--show');
      entry.target.classList.remove('product__sticky--hide');
    } else {
      entry.target.classList.add('product__sticky--hide');
    }
  }
  handleKeydown(event) {
    if (event.key !== 'Tab' || event.defaultPrevented || !this.isStickyShown()) return;
    if (!this.iamForm.isConnected) {
      this.destroy();
      return;
    }
    var focusableElements = this.getStickyFocusableElements();
    if (focusableElements.length === 0) return;
    var firstFocusableElement = focusableElements[0];
    var lastFocusableElement = focusableElements[focusableElements.length - 1];
    var isActiveElementInSticky = this.target.contains(this.document.activeElement);
    if (isActiveElementInSticky) {
      this.handleTabFromSticky(event, focusableElements);
      return;
    }
    if (this.shouldMoveFocusToAdjacentStickyControl(event.shiftKey)) {
      this.bridgePreviousFocusable = this.getFocusableElementBeforeSticky();
      this.bridgeNextFocusable = this.getFocusableElementAfterSticky();
      this.hasVisitedStickyControls = true;
      event.preventDefault();
      this.focusElement(event.shiftKey ? lastFocusableElement : firstFocusableElement);
      return;
    }
    if (event.shiftKey || this.hasVisitedStickyControls || !this.shouldMoveFocusToSticky()) return;
    this.bridgePreviousFocusable = this.getActiveFocusableElement();
    this.bridgeNextFocusable = this.getNextFocusableElement();
    this.hasVisitedStickyControls = true;
    event.preventDefault();
    firstFocusableElement.focus();
  }
  handleTabFromSticky(event, focusableElements) {
    var activeElementIndex = focusableElements.indexOf(this.document.activeElement);
    if (activeElementIndex === -1) return;
    if (event.shiftKey) {
      if (activeElementIndex > 0) {
        event.preventDefault();
        this.focusElement(focusableElements[activeElementIndex - 1]);
        return;
      }
      if (!this.bridgePreviousFocusable) return;
      var previousFocusableElement = this.bridgePreviousFocusable;
      this.clearBridgeFocusElements();
      event.preventDefault();
      this.focusElement(previousFocusableElement);
    } else {
      if (activeElementIndex < focusableElements.length - 1) {
        event.preventDefault();
        this.focusElement(focusableElements[activeElementIndex + 1]);
        return;
      }
      if (!this.bridgeNextFocusable) return;
      var nextFocusableElement = this.bridgeNextFocusable;
      this.clearBridgeFocusElements();
      event.preventDefault();
      this.focusElement(nextFocusableElement);
    }
  }
  shouldMoveFocusToSticky() {
    var activeElement = this.document.activeElement;
    if (!activeElement || activeElement === this.document.body || activeElement === this.document.documentElement) return true;
    if (!this.iamForm.contains(activeElement) || this.target.contains(activeElement)) return false;
    return Boolean(this.target.compareDocumentPosition(activeElement) & Node.DOCUMENT_POSITION_FOLLOWING);
  }
  shouldMoveFocusToAdjacentStickyControl(isShiftTab) {
    var activeElement = this.document.activeElement;
    var nextFocusableElement = isShiftTab ? this.getPreviousAdjacentFocusableElement(activeElement) : this.getNextAdjacentFocusableElement(activeElement);
    return this.target.contains(nextFocusableElement);
  }
  getActiveFocusableElement() {
    var activeElement = this.document.activeElement;
    if (!activeElement || activeElement === this.document.body || activeElement === this.document.documentElement) return null;
    return activeElement;
  }
  getFocusableElements() {
    var container = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.document;
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(element => this.isVisibleFocusableElement(element));
  }
  getStickyFocusableElements() {
    var focusableElements = this.getFocusableElements(this.target);
    var orderedElements = [];
    var usedElements = new Set();
    var addElement = element => {
      if (!element || usedElements.has(element) || !focusableElements.includes(element)) return;
      orderedElements.push(element);
      usedElements.add(element);
    };
    [this.target.querySelector('.quantity-input__button--down'), this.target.querySelector('.quantity-input__input'), this.target.querySelector('.quantity-input__button--up'), this.target.querySelector('#AddToCartSticky'), this.target.querySelector('iam-add-to-cart-button button[type="submit"], iam-add-to-cart-button button[name="add"]')].forEach(addElement);
    focusableElements.filter(element => this.isWishlistElement(element)).forEach(addElement);
    focusableElements.forEach(addElement);
    return orderedElements;
  }
  isWishlistElement(element) {
    return element.matches('#swym-atw-pdp-button, .atw-button, .product__wishlist, .swym-add-to-wishlist-view-product') || Boolean(element.closest('.swym-atw-button-container, .swym-atw-btn-wrapper'));
  }
  getNextFocusableElement() {
    var activeElement = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.document.activeElement;
    var focusableElements = this.getFocusableElements();
    if (!activeElement || activeElement === this.document.body || activeElement === this.document.documentElement) {
      return focusableElements.find(element => this.isAfterSticky(element) && !this.target.contains(element));
    }
    var activeElementIndex = focusableElements.indexOf(activeElement);
    if (activeElementIndex === -1) return null;
    return focusableElements.slice(activeElementIndex + 1).find(element => !this.target.contains(element)) || null;
  }
  getNextAdjacentFocusableElement() {
    var activeElement = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.document.activeElement;
    var focusableElements = this.getFocusableElements();
    var activeElementIndex = focusableElements.indexOf(activeElement);
    if (activeElementIndex === -1) return null;
    return focusableElements[activeElementIndex + 1] || null;
  }
  getPreviousFocusableElement() {
    var activeElement = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.document.activeElement;
    var focusableElements = this.getFocusableElements();
    if (!activeElement || activeElement === this.document.body || activeElement === this.document.documentElement) return null;
    var activeElementIndex = focusableElements.indexOf(activeElement);
    if (activeElementIndex === -1) return null;
    return focusableElements.slice(0, activeElementIndex).reverse().find(element => !this.target.contains(element)) || null;
  }
  getPreviousAdjacentFocusableElement() {
    var activeElement = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.document.activeElement;
    var focusableElements = this.getFocusableElements();
    var activeElementIndex = focusableElements.indexOf(activeElement);
    if (activeElementIndex === -1) return null;
    return focusableElements[activeElementIndex - 1] || null;
  }
  getFocusableElementAfterSticky() {
    var stickyFocusableElements = this.getFocusableElements(this.target);
    return this.getNextFocusableElement(stickyFocusableElements[stickyFocusableElements.length - 1]);
  }
  getFocusableElementBeforeSticky() {
    return this.getPreviousFocusableElement(this.getFocusableElements(this.target)[0]);
  }
  isAfterSticky(element) {
    return Boolean(this.target.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
  }
  isStickyShown() {
    var _this$target;
    if (!((_this$target = this.target) !== null && _this$target !== void 0 && _this$target.classList.contains('product__sticky--show'))) return false;
    var stickyContainer = this.target.querySelector('.product__sticky-container') || this.target;
    var stickyRect = stickyContainer.getBoundingClientRect();
    var viewport = this.document.defaultView;
    return stickyRect.bottom > 0 && stickyRect.right > 0 && stickyRect.top < viewport.innerHeight && stickyRect.left < viewport.innerWidth;
  }
  isVisibleFocusableElement(element) {
    var styles = this.document.defaultView.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
  }
  focusElement(element) {
    if (!(element !== null && element !== void 0 && element.isConnected)) return;
    element.focus();
  }
  clearBridgeFocusElements() {
    this.bridgePreviousFocusable = null;
    this.bridgeNextFocusable = null;
  }
  destroy() {
    var _this$observer;
    (_this$observer = this.observer) === null || _this$observer === void 0 || _this$observer.disconnect();
    this.clearBridgeFocusElements();
    this.document.removeEventListener('keydown', this.handleKeydownRef, true);
  }
}

// -------------------------------------
// Event Listeners
// -------------------------------------

iamotaEventHandler.subscribe('productForm:init', _ref => {
  var {
    iamForm
  } = _ref;
  new StickyCart(iamForm);
}, {
  source: "product-form--sticky.js"
});